/**
 * agentmemory-bridge.ts — lifecycle glue between the plugin and agentmemory
 * (v0.58.0 F2/F3).
 *
 * Three responsibilities, each fire-and-forget and never throwing:
 *
 *  1. TASK CONTEXT (F2) — at the start of every task, ask agentmemory for the
 *     context related to that task (`/context` + `/enrich` for the files about
 *     to change) and return it as a block the caller injects into the agent's
 *     system prompt. One fetch per turn, cached per file-set.
 *
 *  2. SESSION END (F3) — when the task finishes, write the validated lessons
 *     to agentmemory (`/lessons`) and close the session (`/summarize` +
 *     `/session/end`). The 0.56 gates (value gate, novelty, dedupe) still
 *     apply, so this cannot re-introduce the lesson spam.
 *
 *  3. NO DOUBLE CAPTURE — the plugin never observes tool calls for memory
 *     purposes. The agentmemory-capture plugin already does that; two
 *     capturers writing the same events is the duplication this module
 *     removes.
 */

import { execFileSync } from "node:child_process"
import { basename } from "node:path"

import {
  type AgentmemoryClient,
  type AgentmemoryLesson,
  getAgentmemoryClient,
} from "./agentmemory-client.js"
import type { Deviation } from "./types.js"

// ─── F2: task context ──────────────────────────────────────────────

export interface TaskContextRequest {
  readonly sessionID: string
  readonly project: string
  readonly cwd: string
  /** Files the agent is about to touch (optional). */
  readonly files?: readonly string[]
}

export interface TaskContextResult {
  readonly text: string
  readonly tokens: number
  readonly fromFiles: boolean
}

/**
 * Build the agent-facing context block. Returns "" when agentmemory has
 * nothing relevant — zero noise beats a padded prompt.
 */
export function formatTaskContextBlock(
  context: string,
  opts: { files?: readonly string[] } = {},
): string {
  const trimmed = context.trim()
  if (trimmed.length === 0) return ""
  const fileHint =
    opts.files && opts.files.length > 0
      ? ` (for: ${opts.files.slice(0, 3).join(", ")})`
      : ""
  return [
    "",
    "[MEMORY CONTEXT — from agentmemory, cross-session]" + fileHint,
    trimmed,
    "",
    "Cite these when you use them. They are prior context, not current task instructions.",
    "",
  ].join("\n")
}

/**
 * Fetch task context from agentmemory. Tries `/context` (session scope) and,
 * when files are given, `/enrich` (file scope). Degrades to "" on any
 * failure — a down agentmemory means no context, never a crash.
 */
export async function fetchTaskContext(
  client: AgentmemoryClient,
  req: TaskContextRequest,
): Promise<TaskContextResult> {
  try {
    const parts: string[] = []
    let tokens = 0
    let fromFiles = false

    if (req.files && req.files.length > 0) {
      const enriched = await client.enrich({
        sessionId: req.sessionID,
        files: [...req.files].slice(0, 10),
      })
      if (enriched.trim().length > 0) {
        parts.push(enriched.trim())
        fromFiles = true
      }
    }

    const sessionContext = await client.context({
      sessionId: req.sessionID,
      project: req.project,
    })
    if (sessionContext.context.trim().length > 0) {
      parts.push(sessionContext.context.trim())
      tokens = sessionContext.tokens
    }

    return { text: parts.join("\n\n"), tokens, fromFiles }
  } catch {
    return { text: "", tokens: 0, fromFiles: false }
  }
}

// ─── F3: session end ───────────────────────────────────────────────

export interface SessionEndInput {
  readonly sessionID: string
  readonly project: string
  readonly lessons: readonly {
    readonly content: string
    readonly context?: string
    readonly confidence?: number
    readonly tags?: readonly string[]
  }[]
  /** Summarize the session before closing. Default true. */
  readonly summarize?: boolean
}

export interface SessionEndResult {
  readonly lessonsSaved: number
  readonly lessonsStrengthened: number
  readonly lessonsFailed: number
  readonly summarized: boolean
  readonly sessionEnded: boolean
}

/**
 * Persist validated lessons to agentmemory and close the session.
 *
 * agentmemory owns the dedupe: saving a lesson that already exists
 * REINFORCES it (confidence += 0.03) instead of creating a duplicate row.
 * That is exactly the "update existing memories" behaviour we want, and it
 * replaces the plugin's own dedupe layer.
 */
export async function persistSessionEnd(
  client: AgentmemoryClient,
  input: SessionEndInput,
): Promise<SessionEndResult> {
  let lessonsSaved = 0
  let lessonsStrengthened = 0
  let lessonsFailed = 0

  for (const lesson of input.lessons) {
    if (!lesson.content || lesson.content.trim().length === 0) {
      lessonsFailed++
      continue
    }
    try {
      const res = await client.saveLesson({
        content: lesson.content,
        ...(lesson.context ? { context: lesson.context } : {}),
        ...(lesson.confidence !== undefined ? { confidence: lesson.confidence } : {}),
        project: input.project,
        ...(lesson.tags && lesson.tags.length > 0 ? { tags: [...lesson.tags] } : {}),
      })
      if (!res.saved) {
        lessonsFailed++
      } else if (res.action === "strengthened") {
        lessonsStrengthened++
      } else {
        lessonsSaved++
      }
    } catch {
      lessonsFailed++
    }
  }

  let summarized = false
  if (input.summarize !== false) {
    try {
      summarized = await client.summarize(input.sessionID)
    } catch {
      summarized = false
    }
  }

  let sessionEnded = false
  try {
    sessionEnded = await client.endSession(input.sessionID)
  } catch {
    sessionEnded = false
  }

  return { lessonsSaved, lessonsStrengthened, lessonsFailed, summarized, sessionEnded }
}

// ─── Lesson extraction from a finished task ────────────────────────

export interface ExtractLessonsInput {
  readonly deviations: readonly Deviation[]
  readonly filesChanged: readonly string[]
  readonly whatToDo: string
  readonly whereToGo: string
  readonly score?: number
}

/**
 * Build the lessons to persist at session end from the accumulated
 * deviations. Reuses the 0.56 collapse (one line per rule, ×N) so the
 * written lessons never contain N verbatim copies of the same rule.
 */
export function extractLessonsForSession(
  input: ExtractLessonsInput,
  collapse: (deviations: readonly Deviation[]) => Deviation[],
): Array<{ content: string; context: string; confidence: number; tags: string[] }> {
  const collapsed = collapse(input.deviations)
  if (collapsed.length === 0) return []
  const lessons: Array<{ content: string; context: string; confidence: number; tags: string[] }> = []
  for (const deviation of collapsed) {
    const mistake = `${deviation.category}: ${deviation.detail}`.slice(0, 300)
    // v0.59.0 (F1 lesson canónica): the content MUST be byte-identical for
    // the same (rule, where, remedy) triple. The old format embedded the
    // scoring float ("score -0.50" vs "-0.51") and the target file
    // ("seed.ts" vs "bad.ts"), so agentmemory's SHA-256 fingerprint saw 5
    // DIFFERENT lessons where there was one rule — that is the 5-rows-of-
    // garbage class observed in production. Confidence is metadata, not
    // content, and never enters the fingerprint.
    const content = `Mistake: ${mistake}\nWhat to do: ${input.whatToDo}\nWhere: ${input.whereToGo}`.slice(
      0,
      900,
    )
    // Confidence from severity: grave = 0.8, media = 0.65, leve = 0.55.
    const confidence =
      deviation.severity === "grave" ? 0.8 : deviation.severity === "media" ? 0.65 : 0.55
    lessons.push({
      content,
      context: `project:${input.filesChanged.length > 0 ? input.whereToGo : "session"}`,
      confidence,
      tags: [deviation.category],
    })
  }
  return lessons
}

/**
 * Parse the structured block out of a lesson (v0.59.0). Lessons written
 * before v0.59 carry a scoring dump prefix ("Action \"escalate\" (score
 * -0.50) after deviations: ...\nReasoning: ...\n"); the synthesizer must
 * only see the structured part, never the dump.
 */
export interface ParsedLesson {
  readonly mistake: string
  readonly whatToDo: string
  readonly whereToGo: string
}

export function parseLessonContent(content: string): ParsedLesson {
  const marker = content.indexOf("Mistake:")
  const scoped = marker >= 0 ? content.slice(marker) : content
  const mistakeLine = scoped.split("\n").find((l) => l.startsWith("Mistake:")) ?? ""
  const whatLine = scoped.split("\n").find((l) => l.startsWith("What to do:")) ?? ""
  const whereLine = scoped.split("\n").find((l) => l.startsWith("Where:")) ?? ""
  return {
    mistake: mistakeLine.replace(/^Mistake:\s*/, "").trim(),
    whatToDo: whatLine.replace(/^What to do:\s*/, "").trim(),
    whereToGo: whereLine.replace(/^Where:\s*/, "").trim(),
  }
}


/**
 * Resolve the agentmemory project name for a directory — SAME rules as the
 * agentmemory-capture plugin (AGENTMEMORY_PROJECT_NAME > git toplevel
 * basename > cwd basename). Both writers must agree on the project scope or
 * the memories land in different buckets.
 */
export function resolveProjectName(
  dir: string,
  env: Record<string, string | undefined> = process.env,
): string {
  const explicit = (env.AGENTMEMORY_PROJECT_NAME ?? "").trim()
  if (explicit) return explicit
  try {
    const top = execFileSync("git", ["rev-parse", "--show-toplevel"], {
      cwd: dir,
      stdio: ["ignore", "pipe", "ignore"],
      encoding: "utf8",
      windowsHide: true,
    }).trim()
    if (top) return basename(top)
  } catch {
    // not a git repo — fall through
  }
  return basename(dir) || dir
}

// ─── Module-level state (per plugin instance) ─────────────────────

export interface AgentmemoryBridgeState {
  /** Sessions already started in agentmemory. */
  readonly startedSessions: Set<string>
  /** File-sets already enriched (cache key = sorted files). */
  readonly enrichedKeys: Set<string>
  /** Sessions already closed. */
  readonly endedSessions: Set<string>
  /**
   * v0.58.0: lessons already written in this session (dedupe key = rule
   * category). The conscience path (escalate/stop) and the session-end path
   * can both produce a lesson for the same rule; writing it twice would
   * reinforce the same agentmemory lesson and inflate its confidence.
   */
  readonly writtenLessonKeys: Set<string>
}

export function createBridgeState(): AgentmemoryBridgeState {
  return {
    startedSessions: new Set<string>(),
    enrichedKeys: new Set<string>(),
    endedSessions: new Set<string>(),
    writtenLessonKeys: new Set<string>(),
  }
}

/**
 * Write ONE lesson to agentmemory, skipping it when the same rule was
 * already written in this session. Returns the agentmemory action
 * ("created" | "strengthened") or null when skipped/failed.
 */
export async function writeLessonOnce(
  client: AgentmemoryClient,
  state: AgentmemoryBridgeState,
  input: {
    sessionID: string
    project: string
    key: string
    content: string
    context?: string
    confidence?: number
    tags?: string[]
  },
): Promise<{ action?: string; id?: string; confidence?: number; skipped?: boolean }> {
  if (state.writtenLessonKeys.has(input.key)) return { skipped: true }
  state.writtenLessonKeys.add(input.key)
  try {
    const res = await client.saveLesson({
      content: input.content,
      ...(input.context ? { context: input.context } : {}),
      ...(input.confidence !== undefined ? { confidence: input.confidence } : {}),
      project: input.project,
      ...(input.tags && input.tags.length > 0 ? { tags: input.tags } : {}),
    })
    return { action: res.action, id: res.id, confidence: res.confidence }
  } catch {
    return {}
  }
}

/** Stable cache key for a file set. */
export function fileSetKey(files: readonly string[]): string {
  return [...new Set(files)].sort().join("|")
}

/**
 * Ensure the agentmemory session exists (idempotent). Also seeds the session
 * on first use so `/context` has something to work with.
 */
export async function ensureAgentmemorySession(
  client: AgentmemoryClient,
  state: AgentmemoryBridgeState,
  sessionID: string,
  project: string,
  cwd: string,
): Promise<boolean> {
  if (state.startedSessions.has(sessionID)) return true
  state.startedSessions.add(sessionID)
  try {
    return await client.startSession({ sessionId: sessionID, project, cwd })
  } catch {
    return false
  }
}

/** Default client accessor (overridable for tests). */
export function defaultAgentmemoryClient(): AgentmemoryClient {
  return getAgentmemoryClient()
}

export type { AgentmemoryLesson }
